import type { TARSChartConfiguration } from "../tarsBarChartHelpers";
import { normalizeColorInput, parseOptionalColorHex } from "../tarsBarChartHelpers";
import {
  TARS_AXIS_LABEL_FONT_SIZE_PX,
  TARS_DATA_LABEL_FONT_SIZE_PX,
} from "../tars-chart-column-style";
import {
  CHART_TYPE_PICKER_OPTIONS,
  TARS_STACKED_BAR_CHART,
} from "../../notebook-details/analytics/facet-chart-types";
import { TARS_PIVOT_TOTAL_LABEL } from "../../tars-constants";
import { CHART_CONFIG_TITLE_FONT_SIZE_DEFAULT } from "../tarsChartFontHelpers";

export { CHART_CONFIG_TITLE_FONT_SIZE_DEFAULT };

export interface ChartConfigFormModel {
  chartType: string;
  title: string;
  titleFontSize: number;
  axisFontSize: number;
  dataLabelFontSize: number;
  labelColor: string;
  colorByLabel: Record<string, string>;
}

export const CHART_TYPE_OPTIONS = CHART_TYPE_PICKER_OPTIONS;

export function parseChartEntryToFormModel(
  entry: Record<string, unknown> | null
): ChartConfigFormModel {
  if (!entry) return emptyChartConfigFormModel();

  const type = typeof entry.type === "string" ? entry.type : "column-chart";
  const title = typeof entry.title === "string" ? entry.title : "";
  const config = (entry.configuration ?? {}) as TARSChartConfiguration;

  return {
    chartType: type,
    title,
    titleFontSize: config.titleFontSize ?? CHART_CONFIG_TITLE_FONT_SIZE_DEFAULT,
    axisFontSize: config.axisFontSize ?? TARS_AXIS_LABEL_FONT_SIZE_PX,
    dataLabelFontSize: config.dataLabelFontSize ?? TARS_DATA_LABEL_FONT_SIZE_PX,
    labelColor: config.labelColors?.category ?? "#111827",
    colorByLabel: normalizeColorByLabel(
      (config.colorByLabel ?? {}) as Record<string, string>
    ),
  };
}

export function emptyChartConfigFormModel(): ChartConfigFormModel {
  return {
    chartType: "column-chart",
    title: "",
    titleFontSize: CHART_CONFIG_TITLE_FONT_SIZE_DEFAULT,
    axisFontSize: TARS_AXIS_LABEL_FONT_SIZE_PX,
    dataLabelFontSize: TARS_DATA_LABEL_FONT_SIZE_PX,
    labelColor: "#111827",
    colorByLabel: {},
  };
}

function normalizeColorByLabel(map: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [label, raw] of Object.entries(map)) {
    const hex = parseOptionalColorHex(raw) ?? parseOptionalColorHex(normalizeColorInput(raw));
    if (hex) out[label] = hex;
  }
  return out;
}

export function serializeFormModelToChartEntry(
  model: ChartConfigFormModel,
  existingEntry: Record<string, unknown>
): Record<string, unknown> {
  const config: TARSChartConfiguration = {
    colorByLabel: normalizeColorByLabel(model.colorByLabel),
    labelColors: {
      category: parseOptionalColorHex(model.labelColor) ?? normalizeColorInput(model.labelColor),
      value: parseOptionalColorHex(model.labelColor) ?? normalizeColorInput(model.labelColor),
    },
    titleFontSize: model.titleFontSize,
    axisFontSize: model.axisFontSize,
    dataLabelFontSize: model.dataLabelFontSize,
  };

  return {
    ...existingEntry,
    type: model.chartType,
    title: model.title,
    configuration: config,
  };
}

function isPivotTotalLabel(value: unknown): boolean {
  return String(value ?? "").trim().toLowerCase() === TARS_PIVOT_TOTAL_LABEL.toLowerCase();
}

/** Bar/column/donut: row categories. Stacked bar: stack segment headers (legend items). */
export function extractLabelsFromEntry(
  entry: Record<string, unknown> | null,
  chartTypeOverride?: string
): string[] {
  if (!entry) return [];
  const chartType =
    typeof chartTypeOverride === "string" && chartTypeOverride.trim() !== ""
      ? chartTypeOverride
      : typeof entry.type === "string"
        ? entry.type
        : "";

  if (chartType === TARS_STACKED_BAR_CHART) {
    const headers = entry.headers;
    if (!Array.isArray(headers) || headers.length < 2) return [];
    return headers
      .slice(1)
      .map((h) => String(h ?? "").trim())
      .filter((label) => label !== "" && !isPivotTotalLabel(label));
  }

  const rows = entry.rows;
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => (Array.isArray(row) && row.length > 0 ? String(row[0]) : ""))
    .filter((label) => label.trim() !== "" && !isPivotTotalLabel(label));
}

export function chartConfigColorSectionTitle(chartType: string): string {
  return chartType === TARS_STACKED_BAR_CHART ? "Legend Colors" : "Bar / Slice Colors";
}
